import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { api, toEmbedUrl } from "@/api";
import ProductCard from "@/components/ProductCard";
import { CategoryBanner } from "@/components/CategoryBanner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Fallback if admin has not set a URL — empty shows the placeholder card.
const PING_HISTORY_VIDEO_FALLBACK = "";

const META = {
  shop: { base: "/shop", allLabel: "All Products", title: "Pro Shop", kicker: "Bags · Apparel · Accessories",
    intro: "Genuine PING soft goods — bags, apparel, caps, gloves and accessories. Shop online with delivery across India." },
  equipment: { base: "/equipment", allLabel: "All Clubs", title: "Equipment", kicker: "Drivers · Irons · Putters",
    intro: "Shop the full range of PING clubs online — or book a fitting for a fully custom build." },
};

export default function Catalog({ group }) {
  const { category } = useParams();
  const meta = META[group];
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);
  const [categoryBanners, setCategoryBanners] = useState({});
  const [putterSubs, setPutterSubs] = useState([]);
  const [historyVideo, setHistoryVideo] = useState("");

  useEffect(() => {
    api.get("/categories", { params: { group } }).then(({ data }) => setCategories(data));
    api.get("/site-settings").then(({ data }) => {
      setCategoryBanners(data.category_banners || {});
      setPutterSubs(data.putter_subcategories || []);
      setHistoryVideo(data.ping_history_video_url || PING_HISTORY_VIDEO_FALLBACK);
    }).catch(() => {});
  }, [group]);

  useEffect(() => {
    setLoading(true);
    const params = { group };
    if (category) params.category = category;
    if (sort !== "featured") params.sort = sort;
    api.get("/products", { params }).then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, [group, category, sort]);

  const activeCat = categories.find((c) => c.slug === category);
  const title = activeCat ? activeCat.name : meta.title;

  return (
    <div data-testid={`catalog-${group}`}>
      <div className="bg-[#0A0A0A] text-white">
        <div className="container-ping py-14">
          <p className="label-caps text-white/50 mb-3">
            <Link to="/" className="hover:text-white">Home</Link> / {meta.title}{activeCat ? ` / ${activeCat.name}` : ""}
          </p>
          <h1 className="heading-1 text-white">{title}</h1>
          {!activeCat && <p className="text-white/60 mt-4 max-w-xl">{meta.intro}</p>}
        </div>
      </div>

      {activeCat && (
        <CategoryBanner
          categoryName={activeCat.name}
          bgImage={products[0]?.image}
          shopHref={`${meta.base}/${activeCat.slug}`}
          customSlides={categoryBanners[activeCat.slug]}
          featuredProducts={
            products.length
              ? [
                  ...products.filter((p) => p.tag === "NEW" || p.tag === "BESTSELLER"),
                  ...products.filter((p) => p.tag !== "NEW" && p.tag !== "BESTSELLER"),
                ].slice(0, 3)
              : []
          }
          featuredBase={meta.base}
        />
      )}

      {group === "equipment" && category === "putters" && (
        <section className="bg-[#0A0A0A]" data-testid="ping-history-video">
          <div className="container-ping pb-14">
            <p className="label-caps text-white/50 mb-4">Since 1959</p>
            <div className="relative aspect-video w-full overflow-hidden bg-black border border-white/10">
              {historyVideo ? (
                <iframe
                  src={toEmbedUrl(historyVideo)}
                  title="The History of PING"
                  className="absolute inset-0 h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="ping-history-video-iframe"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center gap-3 px-6">
                  <PlayCircle className="h-16 w-16 text-[#E31837]" />
                  <p className="heading-3 text-white">The History of PING</p>
                  <p className="text-sm text-white/50 max-w-md">
                    Video coming soon — the story of Karsten Solheim and the Anser putter that changed golf forever.
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      <div className="container-ping py-10 grid lg:grid-cols-[220px_1fr] gap-10">
        <aside className="hidden lg:block">
          <h4 className="label-caps mb-4">Categories</h4>
          <nav className="flex flex-col gap-1">
            <Link
              to={meta.base}
              className={`py-2 border-b border-border btn-transition ${!category ? "text-[#E31837] font-bold" : "hover:text-[#E31837]"}`}
              data-testid="filter-all"
            >
              {meta.allLabel}
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`${meta.base}/${c.slug}`}
                className={`py-2 border-b border-border flex justify-between btn-transition ${category === c.slug ? "text-[#E31837] font-bold" : "hover:text-[#E31837]"}`}
                data-testid={`filter-${c.slug}`}
              >
                {c.name} <span className="text-muted-foreground text-sm">{c.count}</span>
              </Link>
            ))}
          </nav>
        </aside>

        <div>
          {group === "equipment" && category === "putters" && putterSubs.length > 0 ? (
            <div data-testid="putter-subcategory-hub">
              <p className="label-caps text-[#E31837] mb-2">Shop by Model Family</p>
              <h2 className="heading-3 mb-6">Choose your putter family</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {putterSubs
                  .slice()
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((s) => (
                    <Link
                      key={s.slug}
                      to={`/equipment/putters/${s.slug}`}
                      className="group relative block h-56 overflow-hidden bg-[#0A0A0A]"
                      data-testid={`putter-sub-card-${s.slug}`}
                    >
                      {s.image && (
                        <img
                          src={s.image}
                          alt={s.name}
                          className="absolute inset-0 w-full h-full card-hover-img group-hover:scale-105"
                          style={{ objectFit: s.image_fit || "cover", objectPosition: s.image_position || "center" }}
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-5">
                        <h3 className="heading-3 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)]">{s.name}</h3>
                        <p className="text-white/80 text-xs mt-1 line-clamp-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">{s.description}</p>
                        <span className="mt-3 inline-flex items-center label-caps text-white group-hover:text-[#E31837] btn-transition">Explore →</span>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-muted-foreground" data-testid="results-count">{products.length} products</p>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-[200px] rounded-none" data-testid="sort-select">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="featured">Featured</SelectItem>
                    <SelectItem value="price_asc">Price: Low to High</SelectItem>
                    <SelectItem value="price_desc">Price: High to Low</SelectItem>
                    <SelectItem value="name">Name: A–Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {loading ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="aspect-square bg-secondary animate-pulse" />
                  ))}
                </div>
              ) : products.length === 0 ? (
                <p className="py-20 text-center text-muted-foreground">No products found.</p>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
                  {products.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
